// 每个独立 Section 都会加载此文件，用闭包隔离类声明以避免重复执行冲突。
(() => {
class NewBetterSleep extends HTMLElement {
  #abortController = null;
  #carouselIntervals = new Map();
  #reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  #resultsRevealObserver = null;
  #videoTriggerByDialog = new WeakMap();

  connectedCallback() {
    this.#abortController = new AbortController();
    this.#setupCarousels();
    this.#setupResultsReveal();
    this.#setupExternalVideoCovers();
    this.#setupVideoDialogs();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#clearAllCarouselIntervals();
    this.#resultsRevealObserver?.disconnect();
    this.#resultsRevealObserver = null;
    for (const dialog of this.querySelectorAll('[data-video-dialog]')) {
      this.#clearVideoPlayer(dialog);
      if (dialog.open) dialog.close();
    }
    this.#syncDocumentScrollLock();
  }

  #setupCarousels() {
    for (const carousel of this.querySelectorAll('[data-carousel]')) {
      this.#setupCarousel(carousel);
    }
  }

  #setupCarousel(carousel) {
    const viewport = carousel.querySelector('[data-carousel-viewport]');
    const track = carousel.querySelector('[data-carousel-track]');
    const previousButton = carousel.querySelector('[data-carousel-previous]');
    const nextButton = carousel.querySelector('[data-carousel-next]');
    const slides = [...carousel.querySelectorAll('[data-carousel-slide]')];
    if (!viewport || !track || !previousButton || !nextButton || slides.length === 0) return;

    const interactionPauseReasons = new Set();
    const delay = Number.parseInt(carousel.dataset.autoplayDelay || '6000', 10);

    const getStep = () => {
      const firstSlide = slides[0];
      const trackStyle = window.getComputedStyle(track);
      return firstSlide.getBoundingClientRect().width + Number.parseFloat(trackStyle.columnGap || trackStyle.gap || '0');
    };

    const move = (direction) => {
      const maxScroll = viewport.scrollWidth - viewport.clientWidth;
      if (direction > 0 && viewport.scrollLeft >= maxScroll - 2) {
        viewport.scrollTo({ left: 0, behavior: this.#reducedMotionQuery.matches ? 'auto' : 'smooth' });
        return;
      }
      if (direction < 0 && viewport.scrollLeft <= 2) {
        viewport.scrollTo({ left: maxScroll, behavior: this.#reducedMotionQuery.matches ? 'auto' : 'smooth' });
        return;
      }
      viewport.scrollBy({ left: getStep() * direction, behavior: this.#reducedMotionQuery.matches ? 'auto' : 'smooth' });
    };

    const stopAutoplay = () => {
      const intervalId = this.#carouselIntervals.get(carousel);
      if (intervalId) window.clearInterval(intervalId);
      this.#carouselIntervals.delete(carousel);
    };

    const startAutoplay = () => {
      stopAutoplay();
      if (interactionPauseReasons.size > 0 || this.#reducedMotionQuery.matches || document.hidden) return;
      const intervalId = window.setInterval(() => move(1), Math.max(delay, 5000));
      this.#carouselIntervals.set(carousel, intervalId);
    };

    const pauseForInteraction = (reason) => {
      interactionPauseReasons.add(reason);
      stopAutoplay();
      viewport.setAttribute('aria-live', 'polite');
    };

    const resumeAfterInteraction = (reason) => {
      interactionPauseReasons.delete(reason);
      viewport.setAttribute('aria-live', interactionPauseReasons.size > 0 ? 'polite' : 'off');
      startAutoplay();
    };

    previousButton.addEventListener('click', () => {
      move(-1);
      startAutoplay();
    }, { signal: this.#abortController.signal });

    nextButton.addEventListener('click', () => {
      move(1);
      startAutoplay();
    }, { signal: this.#abortController.signal });

    // 设计稿仅保留方向按钮；自动播放在悬停、聚焦、后台标签页和减少动态效果时暂停。
    carousel.addEventListener('mouseenter', () => pauseForInteraction('pointer'), { signal: this.#abortController.signal });
    carousel.addEventListener('mouseleave', () => resumeAfterInteraction('pointer'), { signal: this.#abortController.signal });
    carousel.addEventListener('focusin', () => pauseForInteraction('focus'), { signal: this.#abortController.signal });
    carousel.addEventListener('focusout', (event) => {
      if (carousel.contains(event.relatedTarget)) return;
      resumeAfterInteraction('focus');
    }, { signal: this.#abortController.signal });

    document.addEventListener('visibilitychange', startAutoplay, { signal: this.#abortController.signal });
    this.#reducedMotionQuery.addEventListener('change', startAutoplay, { signal: this.#abortController.signal });

    startAutoplay();
  }

  #clearAllCarouselIntervals() {
    for (const intervalId of this.#carouselIntervals.values()) {
      window.clearInterval(intervalId);
    }
    this.#carouselIntervals.clear();
  }

  #setupResultsReveal() {
    const resultCards = this.querySelector('[data-results-reveal]');
    const revealTrigger = this.querySelector('[data-results-reveal-trigger]');
    if (!resultCards || !revealTrigger) return;

    // 不支持视口监听时直接显示，避免数据区域因渐进增强失败而不可见。
    if (!('IntersectionObserver' in window)) {
      resultCards.classList.add('new-better-sleep__result-cards--visible');
      return;
    }

    let resultCardsInView = false;
    const syncResultCardsVisibility = () => {
      const shouldShow = this.#reducedMotionQuery.matches || resultCardsInView;
      resultCards.classList.toggle('new-better-sleep__result-cards--visible', shouldShow);
    };

    resultCards.classList.add('new-better-sleep__result-cards--reveal-ready');
    this.#resultsRevealObserver = new IntersectionObserver((entries) => {
      // 持续同步进入和离开状态，使每次重新滑入时都能再次播放淡入动画。
      for (const entry of entries) {
        if (entry.target !== revealTrigger) continue;
        resultCardsInView = entry.isIntersecting;
        syncResultCardsVisibility();
      }
    }, {
      threshold: 0.18,
      rootMargin: '0px 0px -8% 0px'
    });
    // 观察不参与位移动画的外层布局，避免动画改变自身交叉状态而形成反复抖动。
    this.#resultsRevealObserver.observe(revealTrigger);

    this.#reducedMotionQuery.addEventListener('change', () => {
      syncResultCardsVisibility();
    }, { signal: this.#abortController.signal });

    syncResultCardsVisibility();
  }

  #setupExternalVideoCovers() {
    for (const cover of this.querySelectorAll('[data-external-video-cover]')) {
      this.#renderExternalVideoCover(cover);
    }
  }

  #renderExternalVideoCover(cover) {
    const videoData = this.#getExternalVideoData(cover.dataset.videoUrl || '');
    if (!videoData) return;

    const title = cover.dataset.videoTitle || 'Expert video';
    if (videoData.youtubeId) {
      const thumbnailUrl = new URL(`https://i.ytimg.com/vi/${encodeURIComponent(videoData.youtubeId)}/hqdefault.jpg`);
      cover.replaceChildren(this.#createExternalCoverImage(thumbnailUrl, title));
      return;
    }

    if (videoData.vimeoId) {
      void this.#loadVimeoExternalCover(cover, videoData.videoUrl, title);
      return;
    }

    if (videoData.isDirectVideo) {
      cover.replaceChildren(this.#createDirectVideoCover(videoData.videoUrl));
    }
  }

  async #loadVimeoExternalCover(cover, videoUrl, title) {
    const oEmbedUrl = new URL('https://vimeo.com/api/oembed.json');
    oEmbedUrl.searchParams.set('url', videoUrl.href);
    oEmbedUrl.searchParams.set('width', '800');

    try {
      const response = await fetch(oEmbedUrl, {
        headers: { Accept: 'application/json' },
        signal: this.#abortController?.signal
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const payload = await response.json();
      if (typeof payload.thumbnail_url !== 'string' || payload.thumbnail_url.length === 0) return;

      const thumbnailUrl = new URL(payload.thumbnail_url);
      if (!['http:', 'https:'].includes(thumbnailUrl.protocol) || !cover.isConnected) return;
      cover.replaceChildren(this.#createExternalCoverImage(thumbnailUrl, title));
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.warn('[NewBetterSleep] Vimeo 视频封面加载失败。', error);
      }
    }
  }

  #createExternalCoverImage(source, title) {
    const image = document.createElement('img');
    image.src = source.href;
    image.alt = title;
    image.loading = 'lazy';
    image.decoding = 'async';
    image.referrerPolicy = 'strict-origin-when-cross-origin';
    return image;
  }

  #createDirectVideoCover(source) {
    const video = document.createElement('video');
    video.src = source.href;
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.preload = 'metadata';
    video.tabIndex = -1;
    video.setAttribute('aria-hidden', 'true');

    // 元数据可用后轻微定位到开头，促使浏览器解码并显示第一个可用画面。
    video.addEventListener('loadedmetadata', () => {
      if (video.duration > 0) video.currentTime = Math.min(0.01, video.duration);
    }, { once: true, signal: this.#abortController?.signal });
    video.load();
    return video;
  }

  #setupVideoDialogs() {
    for (const dialog of this.querySelectorAll('[data-video-dialog]')) {
      const closeButton = dialog.querySelector('[data-video-dialog-close]');
      const triggers = [...this.querySelectorAll('[data-video-trigger]')]
        .filter((trigger) => trigger.getAttribute('aria-controls') === dialog.id);
      if (!closeButton || triggers.length === 0) continue;

      for (const trigger of triggers) {
        trigger.addEventListener('click', () => this.#openVideoDialog(dialog, trigger), {
          signal: this.#abortController.signal
        });
      }

      closeButton.addEventListener('click', () => dialog.close(), {
        signal: this.#abortController.signal
      });

      // 原生 dialog 已处理 Escape；这里补充点击遮罩关闭和关闭后的播放器清理。
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      }, { signal: this.#abortController.signal });

      dialog.addEventListener('close', () => {
        this.#clearVideoPlayer(dialog);
        this.#syncDocumentScrollLock();
        const trigger = this.#videoTriggerByDialog.get(dialog);
        if (trigger?.isConnected) trigger.focus();
        this.#videoTriggerByDialog.delete(dialog);
      }, { signal: this.#abortController.signal });
    }
  }

  #openVideoDialog(dialog, trigger) {
    const card = trigger.closest('[data-carousel-slide]');
    const player = dialog.querySelector('[data-video-dialog-player]');
    const emptyState = dialog.querySelector('[data-video-dialog-empty]');
    const title = dialog.querySelector('[data-video-dialog-title]');
    const closeButton = dialog.querySelector('[data-video-dialog-close]');
    if (!card || !player || !emptyState || !title || !closeButton || typeof dialog.showModal !== 'function') return;

    this.#clearVideoPlayer(dialog);
    title.textContent = card.querySelector('h3')?.textContent?.trim() || title.textContent;

    const externalPlayer = this.#createExternalVideoPlayer(trigger.dataset.videoUrl || '', title.textContent);
    if (externalPlayer) {
      player.append(externalPlayer);
    }

    const hasPlayer = player.childElementCount > 0;
    player.hidden = !hasPlayer;
    emptyState.hidden = hasPlayer;
    this.#videoTriggerByDialog.set(dialog, trigger);

    dialog.showModal();
    this.#syncDocumentScrollLock();
    closeButton.focus();

    const video = player.querySelector('video');
    if (video) {
      video.play().catch(() => {
        // 浏览器拒绝自动播放时保留原生 controls，用户仍可手动播放。
      });
    }
  }

  #createExternalVideoPlayer(source, title) {
    const videoData = this.#getExternalVideoData(source);
    if (!videoData) return null;

    if (videoData.youtubeId) {
      const embedUrl = new URL(`https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoData.youtubeId)}`);
      embedUrl.searchParams.set('autoplay', '1');
      embedUrl.searchParams.set('rel', '0');
      return this.#createVideoIframe(embedUrl, title);
    }

    if (videoData.vimeoId) {
      const embedUrl = new URL(`https://player.vimeo.com/video/${encodeURIComponent(videoData.vimeoId)}`);
      embedUrl.searchParams.set('autoplay', '1');
      return this.#createVideoIframe(embedUrl, title);
    }

    if (videoData.isDirectVideo) {
      const video = document.createElement('video');
      video.src = videoData.videoUrl.href;
      video.controls = true;
      video.autoplay = true;
      video.playsInline = true;
      video.preload = 'metadata';
      return video;
    }

    return this.#createVideoIframe(videoData.videoUrl, title);
  }

  #getExternalVideoData(source) {
    if (!source) return null;

    let videoUrl;
    try {
      videoUrl = new URL(source, window.location.origin);
    } catch (error) {
      console.warn('[NewBetterSleep] 外部视频链接无效。', error);
      return null;
    }

    if (!['http:', 'https:'].includes(videoUrl.protocol)) return null;

    const hostname = videoUrl.hostname.replace(/^www\./, '').toLowerCase();
    return {
      videoUrl,
      youtubeId: this.#getYouTubeVideoId(videoUrl, hostname),
      vimeoId: this.#getVimeoVideoId(videoUrl, hostname),
      isDirectVideo: /\.(mp4|webm|ogg|m4v)$/i.test(videoUrl.pathname)
    };
  }

  #getYouTubeVideoId(videoUrl, hostname) {
    if (hostname === 'youtu.be') return videoUrl.pathname.split('/').filter(Boolean)[0] || '';
    if (!['youtube.com', 'm.youtube.com'].includes(hostname)) return '';
    if (videoUrl.pathname === '/watch') return videoUrl.searchParams.get('v') || '';
    const pathParts = videoUrl.pathname.split('/').filter(Boolean);
    if (['embed', 'shorts', 'live'].includes(pathParts[0])) return pathParts[1] || '';
    return '';
  }

  #getVimeoVideoId(videoUrl, hostname) {
    if (hostname !== 'vimeo.com' && !hostname.endsWith('.vimeo.com')) return '';
    return videoUrl.pathname.split('/').filter(Boolean).reverse().find((part) => /^\d+$/.test(part)) || '';
  }

  #createVideoIframe(videoUrl, title) {
    const iframe = document.createElement('iframe');
    iframe.src = videoUrl.href;
    iframe.title = title;
    iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
    iframe.allowFullscreen = true;
    iframe.referrerPolicy = 'strict-origin-when-cross-origin';
    return iframe;
  }

  #clearVideoPlayer(dialog) {
    const player = dialog.querySelector('[data-video-dialog-player]');
    const emptyState = dialog.querySelector('[data-video-dialog-empty]');
    if (!player || !emptyState) return;

    for (const video of player.querySelectorAll('video')) {
      video.pause();
      video.removeAttribute('src');
      video.load();
    }
    player.replaceChildren();
    player.hidden = false;
    emptyState.hidden = true;
  }

  #syncDocumentScrollLock() {
    const hasOpenVideoDialog = Boolean(document.querySelector('new-better-sleep [data-video-dialog][open]'));
    document.documentElement.classList.toggle('new-better-sleep-video-open', hasOpenVideoDialog);
  }
}

if (!customElements.get('new-better-sleep')) {
  customElements.define('new-better-sleep', NewBetterSleep);
}
})();
